import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ShoppingCartIcon, UserIcon, HeartIcon, GlobeIcon, MessageCircleIcon, PhoneIcon, XIcon, TrendingUpIcon, PlusIcon, PackageIcon, ClipboardListIcon, MapPinIcon, UserPlusIcon, ChevronRightIcon, TrashIcon, ChevronDownIcon, ClockIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useStore } from '../../lib/store';
import { trackViewCart } from '../../lib/analytics/events';
import { haptics } from '../../lib/utils/haptics';
import { AnnouncementBar } from './AnnouncementBar';
import { AuthModal } from '../modals/AuthModal';
import { LocationModal } from '../modals/LocationModal';
import { OrderTrackingModal } from '../modals/OrderTrackingModal';
import { TRENDING_SEARCHES, TRENDING_PRODUCTS } from '../../data/searchData';
interface DesktopHeaderProps {
  locationModalOpen: boolean;
  setLocationModalOpen: (open: boolean) => void;
  selectedLocation: {
    name: string;
  } | null;
  setSelectedLocation: (location: {
    name: string;
  } | null) => void;
  disableShrink?: boolean;
  showAnnouncement?: boolean;
}
export function DesktopHeader({
  locationModalOpen,
  setLocationModalOpen,
  selectedLocation,
  setSelectedLocation,
  disableShrink = false,
  showAnnouncement = false
}: DesktopHeaderProps) {
  const navigate = useNavigate();
  const {
    cart: cartItems,
    wishlist: wishlistItems,
    language,
    setLanguage,
    cartCount,
    cartTotal,
    searchQuery,
    setSearchQuery,
    addToCart,
    recentSearches,
    addRecentSearch,
    clearRecentSearches
  } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showWishlistMenu, setShowWishlistMenu] = useState(false);
  const [showCartMenu, setShowCartMenu] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [orderTrackingModalOpen, setOrderTrackingModalOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const wishlistCount = wishlistItems.length;
  const cartTotalValue = cartTotal();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target as Node) && searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setShowSearchDropdown(true);
  };
  const handleMouseEnter = (menu: string) => {
    if (menu === 'account') setShowAccountMenu(true);
    if (menu === 'wishlist') setShowWishlistMenu(true);
    if (menu === 'cart') {
      setShowCartMenu(true);
      if (cartItems.length > 0) {
        trackViewCart(cartItems.map(item => ({
          item_id: item.id.toString(),
          item_name: item.name,
          price: item.price,
          quantity: item.quantity
        })));
      }
    }
  };
  const handleMouseLeave = (menu: string) => {
    if (menu === 'account') setShowAccountMenu(false);
    if (menu === 'wishlist') setShowWishlistMenu(false);
    if (menu === 'cart') setShowCartMenu(false);
  };
  const goals = [{
    name: 'Muscle Building',
    icon: '💪',
    slug: 'muscle-building'
  }, {
    name: 'Weight Loss',
    icon: '⚖️',
    slug: 'weight-loss'
  }, {
    name: 'Energy & Endurance',
    icon: '⚡',
    slug: 'energy-endurance'
  }, {
    name: 'Recovery',
    icon: '🔄',
    slug: 'recovery'
  }, {
    name: 'General Health',
    icon: '❤️',
    slug: 'general-health'
  }, {
    name: 'Mass Gain',
    icon: '📈',
    slug: 'mass-gain'
  }];
  const getSearchContent = () => {
    const isSearching = searchQuery.length > 0;
    return <div className="flex flex-col md:flex-row gap-6" data-id="element-2608" data-mp="18gbp02umb1c">
        <div className="flex-1 space-y-6" data-id="element-2609" data-mp="17wwhr4l20r0">
          {!isSearching ? <>
              {recentSearches.length > 0 && <div data-id="element-2610" data-mp="22ztixvsagv0k">
                  <div className="flex items-center justify-between mb-3" data-id="element-2611" data-mp="22za1eg6jw8n2">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2612" data-mp="1yy7zfrl91ihe">
                      Recent Searches
                    </h3>
                    <button onClick={() => clearRecentSearches()} className="text-xs text-[#1E2B55] hover:underline font-medium" data-id="element-2613" data-mp="1out55wmew69k">
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-2614" data-mp="1k2ja626fwg0f">
                    {recentSearches.map((search, index) => <motion.div key={index} initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="flex items-center px-3 py-1.5 rounded-lg bg-gray-50 text-sm hover:bg-gray-100 transition-colors cursor-pointer border border-gray-100 group" onClick={() => setSearchQuery(search)} data-id="element-2615" data-mp="154qwzgod83pe" data-mpi={index}>
                        <ClockIcon className="w-3.5 h-3.5 mr-2 text-gray-400" data-id="element-2616" data-mp="2t5e73ooqft71" />
                        <span className="text-gray-700" data-id="element-2617" data-mp="2p1ol96zfzppp">{search}</span>
                      </motion.div>)}
                  </div>
                </div>}
              <div data-id="element-2618" data-mp="2f5brya7t7dvh">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2619" data-mp="2f5uzcwzu2jv9">
                  Trending Searches
                </h3>
                <div className="grid grid-cols-2 gap-2" data-id="element-2620" data-mp="37t7hmuyyo5ir">
                  {TRENDING_SEARCHES.map((search, index) => <button key={index} onClick={() => setSearchQuery(search)} className="flex items-center p-2 rounded-lg hover:bg-blue-50 transition-colors text-sm text-gray-700 group" data-id="element-2621" data-mp="3317dsrfrssgw" data-mpi={index}>
                      <TrendingUpIcon className="w-4 h-4 mr-3 text-blue-500 group-hover:scale-110 transition-transform" data-id="element-2622" data-mp="3qs3b242zwfvj" />
                      {search}
                    </button>)}
                </div>
              </div>
            </> : <div className="space-y-4" data-id="element-2623" data-mp="6yi040zcr916">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2624" data-mp="bvi5sasai9rr">
                Suggestions
              </h3>
              <div className="space-y-1" data-id="element-2625" data-mp="gw5v8s0veijv">
                {TRENDING_SEARCHES.filter(s => s.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 5).map((search, index) => <button key={index} onClick={() => setSearchQuery(search)} className="flex items-center w-full p-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700" data-id="element-2626" data-mp="ulzlnes0xavp" data-mpi={index}>
                      <SearchIcon className="w-4 h-4 mr-3 text-gray-400" data-id="element-2627" data-mp="14im5c08m5vw5" />
                      <span className="flex-1 text-left" data-id="element-2628" data-mp="18mac4gpf8ngc">{search}</span>
                    </button>)}
              </div>
            </div>}
        </div>
        <div className="w-full md:w-[320px] border-t md:border-t-0 md:border-l border-gray-100 pt-6 md:pt-0 md:pl-6" data-id="element-2629" data-mp="1pucb6iuq3bqj">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2630" data-mp="23un2fmzy2sc8">
            {isSearching ? 'Matching Products' : 'Trending Products'}
          </h3>
          <div className="space-y-4" data-id="element-2631" data-mp="1b7j0xs028w57">
            {TRENDING_PRODUCTS.slice(0, 4).map(product => <div key={product.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-xl transition-colors group cursor-pointer" onClick={() => navigate(`/product/${product.id}`)} data-id="element-2632" data-mp="168fz0gfdn61g">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-100" data-id="element-2633" data-mp="sfzxjk24jg2n">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" data-id="element-2634" data-mp="nhoqti74llon" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-2635" data-mp="33x1rlnugoc86">
                  <h4 className="text-sm font-bold text-gray-900 line-clamp-2 leading-tight mb-1 group-hover:text-[#1E2B55] transition-colors" data-id="element-2636" data-mp="2q4lnejptq85o">
                    {product.name}
                  </h4>
                  <span className="text-sm font-black text-[#1E2B55]" data-id="element-2637" data-mp="3hvt5pa9bpxwb">
                    {product.price.toFixed(2)} SAR
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </div>;
  };
  return <div className="hidden md:block fixed top-0 left-0 right-0 z-[100] transition-all duration-300" data-id="element-2638" data-mp="3dt4w30cscmya">
      {/* Top Header Bar */}
      <motion.div initial={false} animate={{
      height: scrolled ? 0 : 'auto',
      opacity: scrolled ? 0 : 1
    }} transition={{
      duration: 0.3
    }} className="bg-white border-b border-gray-100 overflow-hidden" data-id="element-2639" data-mp="1xen3t3uef38e">
        <div className="container mx-auto px-4" data-id="element-2640" data-mp="12qqyqt2ro97m">
          <div className="flex items-center justify-between py-2.5 text-[11px] text-gray-500 font-medium" data-id="element-2641" data-mp="12pfteotoatee">
            <div className="flex items-center space-x-6" data-id="element-2642" data-mp="owq9cpxpfyjx">
              <button onClick={() => setOrderTrackingModalOpen(true)} className="flex items-center hover:text-[#1E2B55] transition-colors" data-id="element-2643" data-mp="twvsfhpwscqe">
                <PackageIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-2644" data-mp="jzzsi0e2syxg" />
                <span data-id="element-2645" data-mp="jxdbzb97j933">Track My Order</span>
              </button>
              <button className="flex items-center hover:text-[#1E2B55] transition-colors" data-id="element-2646" data-mp="61jbngmi1s1l">
                <PhoneIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-2647" data-mp="azun1vsg0alx" />
                <span data-id="element-2648" data-mp="20e97w50cbdc5">Contact Pharmacist</span>
              </button>
              <button className="flex items-center hover:text-[#1E2B55] transition-colors" data-id="element-2649" data-mp="20bwnzmb0uswg">
                <MessageCircleIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-2650" data-mp="25azwxhu4hj9p" />
                <span data-id="element-2651" data-mp="2fi2evvbq4noc">Ask a Pharmacist</span>
              </button>
            </div>
            <div className="flex items-center" data-id="element-2652" data-mp="2aiplssu9ekdb">
              <div className="relative group" data-id="element-2653" data-mp="2aftnh3ei1toc">
                <button className="flex items-center hover:text-[#1E2B55] transition-colors" data-id="element-2654" data-mp="2o9kwmaobjbjj">
                  <GlobeIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-2655" data-mp="2ji2z5wli7t3b" />
                  <span data-id="element-2656" data-mp="2jgrt1q4l6991">{language === 'ar' ? 'Arabic' : 'English'}</span>
                  <ChevronDownIcon className="w-3 h-3 ml-1" data-id="element-2657" data-mp="2y5xpx7fybndr" />
                </button>
                <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-50 hidden group-hover:block" data-id="element-2658" data-mp="2t7me4r63j2sw">
                  <div className="py-1" data-id="element-2659" data-mp="375ugxfptcybz">
                    <button onClick={() => setLanguage('en')} className={`w-full text-left block px-4 py-2 text-sm ${language === 'en' ? 'bg-blue-50 text-[#1E2B55] font-semibold' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-2660" data-mp="337n1qpb0052w">
                      English
                    </button>
                    <button onClick={() => setLanguage('ar')} className={`w-full text-left block px-4 py-2 text-sm ${language === 'ar' ? 'bg-blue-50 text-[#1E2B55] font-semibold' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-2661" data-mp="37zf7uvt8a5m2">
                      Arabic
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Header */}
      <div className="bg-white border-b border-gray-100" data-id="element-2662" data-mp="3vt6s51qrixad">
        <div className="container mx-auto px-4" data-id="element-2663" data-mp="4hn64ppai7ao">
          <motion.div animate={{
          height: scrolled ? 60 : 72
        }} transition={{
          duration: 0.3
        }} className="flex items-center justify-between" data-id="element-2664" data-mp="4iyaorqk7j2u">
            {/* Logo */}
            <button onClick={() => navigate('/')} className="flex-shrink-0" data-id="element-2665" data-mp="igwmbpdhx1en">
              <motion.img animate={{
              h: scrolled ? 32 : 44
            }} src="https://uploadthingy.s3.us-west-1.amazonaws.com/8onJCNEDDvmSU1TZkPymCw/English.png" alt="DR Nutrition" className={scrolled ? 'h-8' : 'h-11'} data-id="element-2666" data-mp="22zgwn8vrv4nm" />
            </button>

            {/* Deliver To & Search */}
            <div className={`flex-1 ${scrolled ? 'max-w-6xl mx-6' : 'max-w-5xl mx-10'} flex items-center gap-4`} data-id="element-2667" data-mp="2mmwp31wrxleh">
              <button onClick={() => setLocationModalOpen(true)} className={`flex items-center gap-2 px-4 ${scrolled ? 'h-10' : 'h-12'} border border-gray-200 rounded-lg hover:bg-gray-50 transition-all whitespace-nowrap ${scrolled ? 'min-w-[140px]' : 'min-w-[180px]'}`} data-id="element-2668" data-mp="2hntt06yrupin">
                <MapPinIcon className={`${scrolled ? 'w-4 h-4' : 'w-5 h-5'} text-[#1E2B55]`} data-id="element-2669" data-mp="35ez7ahp2d9qu" />
                <div className="text-left" data-id="element-2670" data-mp="2l4eo6532z8x0">
                  {!scrolled && <div className="text-[10px] text-gray-500 leading-none" data-id="element-2671" data-mp="2p5rs29mm521a">
                      Deliver to
                    </div>}
                  <div className={`${scrolled ? 'text-xs' : 'text-sm'} font-bold text-gray-900`} data-id="element-2672" data-mp="2b7a6ahg9pzm0">
                    {selectedLocation ? selectedLocation.name : 'Select Location'}
                  </div>
                </div>
                <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" data-id="element-2673" data-mp="38t7n8af01wvu" />
              </button>

              <div className="flex-1 relative" data-id="element-2674" data-mp="2u4bcu6ph07s2">
                <input type="text" placeholder="Search for supplements, brands, goals..." className={`w-full ${scrolled ? 'h-10' : 'h-12'} border border-gray-200 rounded-lg px-4 pr-12 focus:outline-none focus:ring-2 focus:ring-[#1E2B55]/10 focus:border-[#1E2B55] transition-all`} value={searchQuery} onChange={handleSearchInputChange} onFocus={() => setShowSearchDropdown(true)} ref={searchInputRef} data-id="element-2675" data-mp="2z3nyio4lv8t2" />
                <div className="absolute right-4 top-1/2 -translate-y-1/2" data-id="element-2676" data-mp="1ox1u5ttly6la">
                  <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-2677" data-mp="1kx14fd1268yx" />
                </div>
                <AnimatePresence data-id="element-2678" data-mp="x5vrp6qezwsu">
                  {showSearchDropdown && <motion.div ref={searchDropdownRef} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 max-h-[80vh] overflow-y-auto p-6" data-id="element-2679" data-mp="s7kf4o8qj86x">
                      {getSearchContent()}
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            {/* User Actions */}
            <div className="flex items-center space-x-8" data-id="element-2680" data-mp="1o8g54xwqsxtp">
              <div className={`relative group cursor-pointer flex ${scrolled ? 'flex-row gap-2' : 'flex-col'} items-center`} onMouseEnter={() => handleMouseEnter('account')} onMouseLeave={() => handleMouseLeave('account')} onClick={() => navigate('/account')} data-id="element-2681" data-mp="rj8i1lgh6gno">
                <UserIcon className="w-5 h-5 text-gray-600 group-hover:text-[#1E2B55]" data-id="element-2682" data-mp="15boluoh7aipn" />
                <span className={`${scrolled ? 'text-xs' : 'text-[10px] mt-1.5'} font-bold text-gray-500 uppercase tracking-tight`} data-id="element-2683" data-mp="7w10jes1cv5d">
                  Account
                </span>
                {showAccountMenu && <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 z-50 py-2" data-id="element-2684" data-mp="58gt1vj0upfs">
                    <div className="px-4 py-3 border-b border-gray-100" data-id="element-2685" data-mp="3rh9q8fovg15z">
                      <p className="text-sm font-bold" data-id="element-2686" data-mp="3ngg15izp0gbi">Hello, Guest</p>
                      <button onClick={() => setAuthModalOpen(true)} className="text-xs text-[#1E2B55] font-bold mt-1" data-id="element-2687" data-mp="3ng679kbj1p0j">
                        Sign In / Register
                      </button>
                    </div>
                    <div className="py-1" data-id="element-2688" data-mp="38s35yo8fj1i2">
                      <button onClick={() => navigate('/account')} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left" data-id="element-2689" data-mp="33sqj43hk5ufh">
                        <UserIcon className="w-4 h-4 mr-3" data-id="element-2690" data-mp="2ut3e77pme6ho" />
                        My Account
                      </button>
                      <button onClick={() => navigate('/account/orders')} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left" data-id="element-2691" data-mp="mjvo98uh2t59">
                        <PackageIcon className="w-4 h-4 mr-3" data-id="element-2692" data-mp="hjg8ybqmrbkn" />
                        My Orders
                      </button>
                    </div>
                  </div>}
              </div>

              <div className={`relative group cursor-pointer flex ${scrolled ? 'flex-row gap-2' : 'flex-col'} items-center`} onClick={() => navigate('/account/wishlist')} data-id="element-2693" data-mp="8jkqrmynqgb4">
                <div className="relative" data-id="element-2694" data-mp="3uznpblcesqon">
                  <HeartIcon className="w-5 h-5 text-gray-600 group-hover:text-[#1E2B55]" data-id="element-2695" data-mp="3gz4hofbuvmn0" />
                  <span className="absolute -top-1.5 -right-1.5 bg-[#1E2B55] text-white text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center border border-white" data-id="element-2696" data-mp="3h1gyuq9shsz0">
                    {wishlistCount}
                  </span>
                </div>
                <span className={`${scrolled ? 'text-xs' : 'text-[10px] mt-1.5'} font-bold text-gray-500 uppercase tracking-tight`} data-id="element-2697" data-mp="2xevwwtmhnwf6">
                  Wishlist
                </span>
              </div>

              <div className={`relative group cursor-pointer flex ${scrolled ? 'flex-row gap-2' : 'flex-col'} items-center`} onMouseEnter={() => handleMouseEnter('cart')} onMouseLeave={() => handleMouseLeave('cart')} onClick={() => navigate('/cart')} data-id="element-2698" data-mp="3lba0txk6uu22">
                <div className="relative" data-id="element-2699" data-mp="3q1qe9ho0efof">
                  <ShoppingCartIcon className="w-5 h-5 text-gray-600 group-hover:text-[#1E2B55]" data-id="element-2700" data-mp="3q435klfnq2e5" />
                  <span className="absolute -top-1.5 -right-1.5 bg-[#1E2B55] text-white text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center border border-white" data-id="element-2701" data-mp="7uaovd4tbh7s">
                    {cartCount()}
                  </span>
                </div>
                <span className={`${scrolled ? 'text-xs' : 'text-[10px] mt-1.5'} font-bold text-gray-500 uppercase tracking-tight`} data-id="element-2702" data-mp="bddr56c1w01k">
                  Cart
                </span>
                {showCartMenu && cartItems.length > 0 && <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 z-50 p-4" data-id="element-2703" data-mp="3sudqi0wi6yf1">
                    <div className="max-h-60 overflow-y-auto mb-4" data-id="element-2704" data-mp="3nxewdicuargu">
                      {cartItems.map(item => <div key={item.id} className="flex gap-3 mb-3" data-id="element-2705" data-mp="6hf5hqi3ww4u">
                          <img src={item.image} className="w-12 h-12 rounded object-cover" data-id="element-2706" data-mp="1nu5jswyds30" />
                          <div className="flex-1 min-w-0" data-id="element-2707" data-mp="35647t7ul6qs4">
                            <p className="text-xs font-bold truncate" data-id="element-2708" data-mp="353i0n9blydaf">
                              {item.name}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-2709" data-mp="1pea5zag196i2">
                              {item.quantity} x {item.price} SAR
                            </p>
                          </div>
                        </div>)}
                    </div>
                    <div className="border-t pt-3" data-id="element-2710" data-mp="23e1bemshhekg">
                      <div className="flex justify-between font-bold mb-3" data-id="element-2711" data-mp="23eb26clcrpr7">
                        <span data-id="element-2712" data-mp="28btduaqr8j2w">Total:</span>
                        <span data-id="element-2713" data-mp="28as0fzm9nki0">{cartTotalValue.toFixed(2)} SAR</span>
                      </div>
                      <button onClick={() => navigate('/checkout')} className="w-full bg-[#1E2B55] text-white py-2 rounded-lg font-bold text-sm" data-id="element-2714" data-mp="1uf7knd1u3iet">
                        Checkout
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Navigation Bar */}
      <motion.div initial={false} animate={{
      height: scrolled ? 0 : 52,
      opacity: scrolled ? 0 : 1
    }} transition={{
      duration: 0.3
    }} className="bg-white border-b border-gray-100 overflow-hidden" data-id="element-2715" data-mp="2ko57tmm28gw0">
        <div className="container mx-auto px-4" data-id="element-2716" data-mp="26w8mc1ewd58l">
          <div className="flex items-center h-[52px]" data-id="element-2717" data-mp="26x0hxc8hv2yv">
            <button onClick={() => navigate('/categories')} className="bg-gradient-to-r from-[#8E2DE2] to-[#F000B8] text-white text-xs font-black px-5 h-9 rounded-lg flex items-center gap-2 shadow-sm hover:shadow-md transition-all uppercase tracking-wide" data-id="element-2718" data-mp="21wcq4ocfyi3l">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-2719" data-mp="3rbu3f9xb7vbu" /> All Categories
            </button>
            <nav className="flex items-center ml-10 space-x-10" data-id="element-2720" data-mp="3n9fj0ldzsnig">
              {['Proteins', 'Vitamins', 'Pre-Workout', 'Weight Management'].map(item => <button key={item} onClick={() => navigate(`/category/${item.toLowerCase().replace(/ /g, '-')}`)} className="text-[13px] font-extrabold text-gray-700 hover:text-[#1E2B55] transition-colors" data-id="element-2721" data-mp="1e1ars7df6t2f">
                    {item}
                  </button>)}
              <button onClick={() => navigate('/category/best-sellers')} className="flex items-center gap-2 text-[13px] font-extrabold text-gray-700 hover:text-[#1E2B55]" data-id="element-2722" data-mp="v2yeqaje0z1k">
                Best Sellers
                <span className="bg-[#00A651] text-white text-[8px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-tighter" data-id="element-2723" data-mp="2ajfc3yh92908">
                  POPULAR
                </span>
              </button>
              <button onClick={() => navigate('/offers')} className="flex items-center gap-2 text-[13px] font-extrabold text-gray-700 hover:text-[#1E2B55]" data-id="element-2724" data-mp="2e0kxy4ptbcnn">
                Deals & Offers
                <span className="bg-[#FF4757] text-white text-[8px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-tighter" data-id="element-2725" data-mp="2n2kkfcjlj8xz">
                  SAVE
                </span>
              </button>
            </nav>
          </div>
        </div>
      </motion.div>

      {/* Shop by Goal Section */}
      <motion.div initial={false} animate={{
      height: scrolled ? 0 : 64,
      opacity: scrolled ? 0 : 1
    }} transition={{
      duration: 0.3
    }} className="bg-white border-b border-gray-100 overflow-hidden" data-id="element-2726" data-mp="2s0ck4sqhfzcq">
        <div className="container mx-auto px-4" data-id="element-2727" data-mp="3frhxn18yafjv">
          <div className="flex items-center h-16" data-id="element-2728" data-mp="3fo3uhqsgq2bd">
            <span className="text-[13px] font-black text-gray-900 mr-10" data-id="element-2729" data-mp="3asevppo9opg4">
              Shop by Goal:
            </span>
            <div className="flex gap-4 overflow-x-auto scrollbar-hide py-2" data-id="element-2730" data-mp="3udyj37vcfhdh">
              {goals.map(goal => <button key={goal.slug} onClick={() => navigate(`/category/${goal.slug}`)} className="flex items-center gap-3 px-6 py-2.5 rounded-full bg-[#f8f9fa] hover:bg-white hover:shadow-md border border-gray-100 transition-all whitespace-nowrap group" data-id="element-2731" data-mp="34rn7w9s881x">
                  <span className="text-base group-hover:scale-110 transition-transform" data-id="element-2732" data-mp="3gdw4pvm8v62z">
                    {goal.icon}
                  </span>
                  <span className="text-[12px] font-extrabold text-gray-700 group-hover:text-[#1E2B55]" data-id="element-2733" data-mp="31q2m021v3ng6">
                    {goal.name}
                  </span>
                </button>)}
            </div>
          </div>
        </div>
      </motion.div>

      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} data-id="element-2734" data-mp="2xn630a8etpbp" />
      <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={setSelectedLocation} data-id="element-2735" data-mp="137psd8ae2699" />
      <OrderTrackingModal isOpen={orderTrackingModalOpen} onClose={() => setOrderTrackingModalOpen(false)} data-id="element-2736" data-mp="tguuf6ns5i7z" />
    </div>;
}